import { useEffect, useCallback } from 'react';
import { useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { getAccessToken } from 'src/services/api';
import { isFromCareConnect } from 'src/lib/queryParams';
import { useChatValidationStore } from '../chat';
import { logger } from 'src/services/logger';

type Config = {
  enabled: boolean;
  language?: string;
  onMessage: (data: string, type: 'partial' | 'final') => void;
  onError: (error: unknown) => void;
  onConnect?: (data: unknown) => void;
  onDisconnect?: () => void;
};

const reconnectionAttempts = 5;
const reconnectionDelay = 1000;
const reconnectionDelayMax = 5000;

/**
 * Handles messages received from the API according to our protocol
 * https://docs.rev.ai/api/streaming/responses/
 * @param {MessageEvent} event
 */
interface RevAIResponse {
  type: 'connected' | 'partial' | 'final';
  elements: Array<{ value: string }>;
}

const useSpeechToText = ({
  enabled,
  language,
  onMessage,
  onError,
  onConnect,
  onDisconnect,
}: Config) => {
  const { token } = useChatValidationStore();
  const socketRef = useRef<Socket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const scriptNodeRef = useRef<ScriptProcessorNode | null>(null);
  const inputRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const reconnectionAttemptRef = useRef(0);

  const parseResponse = useCallback((response: RevAIResponse): string => {
    var message = '';
    for (var i = 0; i < response.elements.length; i++) {
      message +=
        response.type == 'final'
          ? response.elements[i].value
          : `${response.elements[i].value} `;
    }
    return message;
  }, []);

  const handleResponse = useCallback((response: RevAIResponse) => {
    switch (response?.type) {
      case 'connected':
        onConnect?.(response);
        break;
      case 'partial':
        onMessage(parseResponse(response), 'partial');
        break;
      case 'final':
        onMessage(parseResponse(response), 'final');
        break;
      default:
        // We expect all messages from the API to be one of these types
        logger.error('onMessage - Received unexpected message', { response });
        break;
    }
  }, []);

  /**
   * Transform an audio processing event into a form suitable to be sent to the API. (S16LE or Signed 16 bit Little Edian).
   * Then send.
   * @param {AudioProcessingEvent} e
   */
  const processAudioEvent = useCallback((e: AudioProcessingEvent): void => {
    if (
      audioContextRef.current?.state === 'suspended' ||
      audioContextRef.current?.state === 'closed' ||
      !socketRef.current
    ) {
      return;
    }

    let inputData = e.inputBuffer.getChannelData(0);

    // The samples are floats in range [-1, 1]. Convert to PCM16le.
    let output = new DataView(new ArrayBuffer(inputData.length * 2));
    for (let i = 0; i < inputData.length; i++) {
      let multiplier = inputData[i] < 0 ? 0x8000 : 0x7fff; // 16-bit signed range is -32768 to 32767
      output.setInt16(i * 2, (inputData[i] * multiplier) | 0, true); // index, value, little edian
    }

    let intData = new Int16Array(output.buffer);
    let index = intData.length;
    while (index-- && intData[index] === 0 && index > 0) {}

    socketRef.current?.emit(
      'audio',
      JSON.stringify({
        audio: Object.values(intData.slice(0, index + 1)),
      })
    );
  }, []);

  const startStream = () => {
    // Create a dedicated AudioContext for STT with different sample rate to avoid conflicts
    audioContextRef.current = new (window.AudioContext ||
      window.WebkitAudioContext)({
      latencyHint: 'interactive', // Best for real-time speech recognition
    });
    socketRef.current = io(
      import.meta.env.VITE_API_BASE_URL + '/speech_to_text',
      {
        path: '/vca/socket.io/',
        query: {
          language,
        },
        withCredentials: true,
        extraHeaders: {
          Authorization: `Bearer ${getAccessToken()}`,
          'From-Care-Connect': isFromCareConnect() ? 'true' : 'false',
          'Chat-URL': token ? `/${token}` : '/',
        },
        closeOnBeforeunload: true,
        reconnection: true, // Enable automatic reconnection
        reconnectionAttempts, // Maximum number of reconnection attempts
        reconnectionDelay, // Initial delay for reconnection (ms)
        reconnectionDelayMax, // Maximum delay between reconnections (ms)
      }
    );

    socketRef.current.on('connect', () => {
      onConnect?.(null);
    });

    socketRef.current.on('connect_error', (error) => {
      reconnectionAttemptRef.current += 1;
      logger.error(
        'useSpeechToText: Socket connection error',
        {
          eventName: 'connect_error',
        },
        error
      );
      if (reconnectionAttemptRef.current >= reconnectionAttempts) {
        onError(error);
      }
    });

    socketRef.current.on('disconnect', () => {
      onDisconnect?.();
    });

    socketRef.current.on('response', (data) => {
      handleResponse(data);
    });

    socketRef.current.on('error', (error) => {
      logger.error(
        'useSpeechToText: Socket connection error',
        {
          eventName: 'error',
        },
        error
      );
      onError(error);
    });

    navigator.mediaDevices
      .getUserMedia({
        audio: true,
      })
      .then((stream) => {
        if (!audioContextRef.current) {
          logger.error('AudioContext is not initialized');
          return;
        }
        micStreamRef.current = stream;
        audioContextRef.current.suspend();

        scriptNodeRef.current = audioContextRef.current.createScriptProcessor(
          4096,
          1,
          1
        );
        inputRef.current = audioContextRef.current.createMediaStreamSource(
          micStreamRef.current
        );

        inputRef.current.connect(scriptNodeRef.current);

        scriptNodeRef.current.addEventListener('audioprocess', (event) => {
          processAudioEvent(event);
        });

        scriptNodeRef.current.connect(audioContextRef.current.destination);
        audioContextRef.current.resume();

        window.isMicOn = true;
      })
      .catch((error) => {
        logger.error(
          'useSpeechToText: Failed to get user media',
          {
            message: error?.message,
          },
          error
        );
        onError(error);
      });
  };

  const stopStream = useCallback(() => {
    window.isMicOn = false;

    if (scriptNodeRef.current) {
      scriptNodeRef.current.removeEventListener(
        'audioprocess',
        processAudioEvent
      );
      scriptNodeRef.current.disconnect();
    }

    if (inputRef.current) {
      inputRef.current.disconnect();
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }

    if (socketRef.current) {
      socketRef.current.close();
    }
  }, [processAudioEvent]);

  const resetStream = useCallback(() => {
    socketRef.current?.emit('reset');
  }, []);

  useEffect(() => {
    if (enabled) {
      startStream();
    } else {
      stopStream();
    }
  }, [enabled]);

  useEffect(() => {
    return stopStream;
  }, []);

  return { startStream, stopStream, resetStream };
};

export { useSpeechToText };
